/**
 * Shared constants for ballot initiative forms and display.
 */

export const MEASURE_TYPES = [
  { value: 'unspecified', label: 'Unspecified' },
  { value: 'initiative', label: 'Statutory Initiative (Citizen-Initiated)' },
  { value: 'constitutional_amendment', label: 'Constitutional Amendment (Citizen-Initiated)' },
  { value: 'referred_constitutional_amendment', label: 'Constitutional Amendment (Legislature-Referred)' },
  { value: 'proposition', label: 'Proposition (Citizen-Initiated)' },
  { value: 'referred_measure', label: 'Referred Measure' },
  { value: 'local_question', label: 'Local Ballot Question' },
];

export const MEASURE_TYPE_VALUES = new Set(MEASURE_TYPES.map(t => t.value));

export function measureTypeLabel(value: string | null | undefined): string {
  const found = MEASURE_TYPES.find(t => t.value === value);
  return found?.label ?? value ?? '';
}

/**
 * Compact type labels for table chips and cards — the full MEASURE_TYPES labels
 * ("Statutory Initiative (Citizen-Initiated)") belong in filter dropdowns, not
 * in a chip. `unspecified` deliberately has no entry: it renders as muted
 * "Not classified" text, not a chip (PRD §5.5).
 */
export const MEASURE_TYPE_SHORT_LABELS: Record<string, string> = {
  initiative: 'Statutory',
  constitutional_amendment: 'Constitutional',
  referred_constitutional_amendment: 'Referred Constitutional',
  proposition: 'Proposition',
  referred_measure: 'Referred',
  local_question: 'Local',
};

export function measureTypeShortLabel(value: string | null | undefined): string {
  if (!value) return '';
  return MEASURE_TYPE_SHORT_LABELS[value] ?? measureTypeLabel(value);
}

export const INITIATIVE_SOURCES = [
  { value: 'citizen', label: 'Citizen-Initiated' },
  { value: 'legislature', label: 'Legislature Referred' },
  { value: 'governor', label: 'Governor Referred' },
  { value: 'county', label: 'County' },
  { value: 'municipality', label: 'Municipality' },
  { value: 'special_district', label: 'Special District' },
];

export function initiativeSourceLabel(value: string | null | undefined): string {
  const found = INITIATIVE_SOURCES.find(s => s.value === value);
  return found?.label ?? value ?? '';
}

/**
 * A Colorado measure's public lifecycle. The pre-ballot values mirror the stages the
 * Secretary of State publishes — filing, Title Board, circulation, signature
 * verification — which is exactly the window advocacy clients need warning in.
 *
 * Mirrors backend/src/constants/ballotInitiativeStatuses.js. BALLOT_VISIBLE is what
 * gates the citizen ballot and the public feed; keep the two files in step.
 */
export const PRE_BALLOT_STATUSES = [
  'proposed', 'filed', 'title_set', 'title_denied',
  'circulating', 'signature_review', 'insufficient',
] as const;

export const BALLOT_VISIBLE_STATUSES = ['qualified', 'on_ballot', 'passed', 'failed'] as const;

export const TERMINAL_NEGATIVE_STATUSES = ['withdrawn', 'expired'] as const;

export const INITIATIVE_STATUSES = [
  { value: 'proposed', label: 'Proposed' },
  { value: 'filed', label: 'Filed' },
  // Labels match the SOS Initiative Filings page's own group headings verbatim
  // (sos.state.co.us/pubs/elections/Initiatives/titleBoard/), so entry from that
  // page needs no translation. Statuses the SOS page has no heading for
  // (proposed, filed, insufficient, qualified, passed, failed) keep our wording.
  { value: 'title_set', label: 'Title set' },
  { value: 'title_denied', label: 'Denied title setting' },
  { value: 'circulating', label: 'Approved for circulation' },
  { value: 'signature_review', label: 'Signature line review' },
  { value: 'insufficient', label: 'Insufficient Signatures' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'on_ballot', label: 'On ballot' },
  { value: 'passed', label: 'Passed' },
  { value: 'failed', label: 'Failed' },
  { value: 'withdrawn', label: 'Withdrawn' },
  { value: 'expired', label: 'Expired' },
];

/** Filter options for "where is this in the pipeline", coarser than a single status. */
export const STATUS_GROUPS = [
  { value: 'pre_ballot', label: 'Pre-ballot (not on any ballot)' },
  { value: 'ballot_visible', label: 'On the ballot' },
  { value: 'terminal_negative', label: 'Ended without ballot' },
];

export const isBallotVisible = (status: string): boolean =>
  (BALLOT_VISIBLE_STATUSES as readonly string[]).includes(status);

/**
 * Editorial release state, independent of lifecycle status. Mirrors
 * backend/src/constants/ballotInitiativeStatuses.js PUBLICATION_STATUSES (PRD v2 §5.1).
 * A measure is publicly visible only when its status is ballot-visible AND a system
 * admin has published it — `isPubliclyVisible` below.
 */
export const PUBLICATION_STATUSES = [
  { value: 'imported', label: 'Imported' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
] as const;
export type PublicationStatus = typeof PUBLICATION_STATUSES[number]['value'];

/** Admin list filter value for "on the ballot by status, not yet published" (§5.3.5). */
export const NEEDS_PUBLISH_FILTER = 'needs_publish';

export function publicationLabel(value: string | null | undefined): string {
  return PUBLICATION_STATUSES.find((s) => s.value === value)?.label ?? value ?? '';
}

/** Chip `tone` for a publication state (Chip's `variant` axis is deprecated). */
export function publicationTone(value: string | null | undefined): 'positive' | 'caution' | 'neutral' {
  switch (value) {
    case 'published': return 'positive';
    case 'imported': return 'caution';
    default: return 'neutral';
  }
}

export const isPublished = (publicationStatus: string | null | undefined): boolean =>
  publicationStatus === 'published';

export const isPubliclyVisible = (row: { status: string; publication_status?: string | null }): boolean =>
  isBallotVisible(row.status) && isPublished(row.publication_status);

export const STATUS_VALUES = new Set(INITIATIVE_STATUSES.map(s => s.value));

export function statusLabel(value: string | null | undefined): string {
  const found = INITIATIVE_STATUSES.find(s => s.value === value);
  return found?.label ?? value ?? '';
}

export function statusVariant(status: string): 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'primary' {
  switch (status) {
    case 'passed': return 'success';
    case 'on_ballot': return 'info';
    case 'qualified': return 'primary';
    case 'circulating': return 'warning';
    case 'signature_review': return 'warning';
    case 'proposed': return 'neutral';
    case 'filed': return 'neutral';
    case 'title_set': return 'neutral';
    case 'title_denied': return 'danger';
    case 'insufficient': return 'danger';
    case 'failed': return 'danger';
    case 'withdrawn': return 'warning';
    case 'expired': return 'warning';
    default: return 'neutral';
  }
}

export const JURISDICTION_LEVELS = [
  { value: 'statewide', label: 'Statewide' },
  { value: 'county', label: 'County' },
  { value: 'municipal', label: 'Municipal' },
  { value: 'special_district', label: 'Special District' },
  { value: 'district', label: 'Legislative District' },
];

export function jurisdictionLabel(value: string | null | undefined): string {
  const found = JURISDICTION_LEVELS.find(j => j.value === value);
  return found?.label ?? value ?? '';
}

export const TRACKING_POSITIONS = [
  { value: 'support', label: 'Support' },
  { value: 'oppose', label: 'Oppose' },
  { value: 'monitor', label: 'Monitor' },
  { value: 'neutral', label: 'Neutral' },
];

export const COMMITTEE_TYPES = [
  { value: 'proponent', label: 'Proponent' },
  { value: 'opponent', label: 'Opponent' },
  // Registered but unaligned. Admin-only, and excluded from the two-sided rollups:
  // a "neutral" column on a support/oppose view confuses more than it informs.
  { value: 'neutral', label: 'Neutral' },
];

/** The two sides a measure actually has. Neutral belongs to neither. */
export const SIDED_COMMITTEE_TYPES = ['proponent', 'opponent'];

export function committeeTypeLabel(value: string | null | undefined): string {
  const found = COMMITTEE_TYPES.find(c => c.value === value);
  return found?.label ?? value ?? '';
}

/**
 * Citizen-content provenance (PRD §5.6).
 *
 * Mirrors backend/src/constants/ballotInitiativeContent.js. The declaration is
 * mandatory with no default selection: a pre-selected option is what recreates the
 * "nobody chose" state, and an unlabelled field only means something if choosing was
 * unavoidable.
 */
/** The fields the Citizen Content card edits, each with its own declaration — in the
 *  reader's order; the admin card and the citizen card both follow it (PRD v2 §5.10). */
export const CITIZEN_CONTENT_FIELDS = [
  'plain_language_summary', 'plain_language_long_description',
  'what_yes_means', 'what_no_means',
  'who_supports_and_why', 'who_opposes_and_why',
  'background_context',
] as const;

/** The fiscal pair — one declaration covers both (v2 PRD §5.6.2). */
export const FISCAL_CONTENT_FIELDS = ['fiscal_impact_statement', 'fiscal_impact_detail'] as const;

/** Every field that carries an origin; mirrors the backend list exactly. */
export const DECLARABLE_FIELDS = [...CITIZEN_CONTENT_FIELDS, ...FISCAL_CONTENT_FIELDS] as const;

export type CitizenContentField = typeof CITIZEN_CONTENT_FIELDS[number];
export type DeclarableField = typeof DECLARABLE_FIELDS[number];

export const CONTENT_ORIGINS = [
  {
    value: 'staff_written',
    label: 'Staff-written',
    hint: 'Written or paraphrased by our team.',
  },
  {
    value: 'ai_reviewed',
    label: 'AI-assisted',
    hint: 'Drafted with an AI tool, then reviewed by you before saving.',
  },
  {
    value: 'official_source',
    label: 'Official source (verbatim)',
    hint: 'Reproduced word-for-word from the Blue Book or another official publication.',
  },
];

export interface ProvenanceEntry {
  origin: 'staff_written' | 'ai_reviewed' | 'official_source';
  tool?: string;
  source?: string;
  reviewed_by?: string;
  reviewed_at?: string;
}

/** The label a reader sees. Staff-written carries none — that is the point of making
 *  the declaration mandatory, so silence is itself a claim. */
export function provenanceLabel(entry: ProvenanceEntry | undefined): string | null {
  if (!entry) return null;
  if (entry.origin === 'ai_reviewed') {
    return entry.tool ? `AI-assisted (${entry.tool}), staff-reviewed` : 'AI-assisted, staff-reviewed';
  }
  if (entry.origin === 'official_source') {
    return entry.source ? `Verbatim from ${entry.source}` : 'Verbatim from an official source';
  }
  return null;
}

/** Amendment 71 (2016) applies by what a measure amends, not who referred it. */
export const CONSTITUTIONAL_MEASURE_TYPES = [
  'constitutional_amendment', 'referred_constitutional_amendment',
];

export const CONSTITUTIONAL_THRESHOLD_PCT = 55;
export const STATUTORY_THRESHOLD_PCT = 50;

export const defaultThresholdFor = (measureType: string): number =>
  (CONSTITUTIONAL_MEASURE_TYPES.includes(measureType)
    ? CONSTITUTIONAL_THRESHOLD_PCT
    : STATUTORY_THRESHOLD_PCT);

/** Null when undecided — an uncounted measure has not failed. */
export function measurePassed(
  votesYes: number | null, votesNo: number | null, thresholdPct: number | null
): boolean | null {
  if (votesYes == null || votesNo == null || votesYes + votesNo === 0) return null;
  const pct = (votesYes / (votesYes + votesNo)) * 100;
  return pct >= (thresholdPct ?? STATUTORY_THRESHOLD_PCT);
}

export function yesPercentage(votesYes: number | null, votesNo: number | null): number | null {
  if (votesYes == null || votesNo == null || votesYes + votesNo === 0) return null;
  return Math.round((votesYes / (votesYes + votesNo)) * 1000) / 10;
}
